import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  Field,
  FormError,
  IconInfo,
  IconKey,
  IconShieldCheck,
  Input,
  Skeleton,
  useToast,
} from '@/design-system';
import { authApi, queryKeys, toUserMessage } from '@/lib/api';
import { formatRegulatedDateTime, formatRelative } from '@/lib/format/datetime';
import { SYSTEM_ROLE_LABEL } from '@/lib/i18n';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * Perfil e segurança da conta.
 *
 * Concentra o que o titular pode fazer sozinho: conferir os próprios dados,
 * trocar a senha e gerenciar o segundo fator. Nome, cargo e papel são somente
 * leitura — alterá-los é ato administrativo, auditado, feito na tela de
 * usuários. O CARGO em especial é congelado em cada assinatura (§11.50(a)),
 * então mudá-lo pelo próprio usuário seria um caminho para adulterar como ele
 * aparece num manifesto futuro.
 */
export function ProfilePage() {
  const { user } = useAuth();
  const timeZone = useOrganizationTimeZone();
  const [enrolling, setEnrolling] = useState(false);

  const profileQuery = useQuery({
    queryKey: queryKeys.session.profile(),
    queryFn: () => authApi.profile(),
  });

  if (profileQuery.isPending) {
    return (
      <PageBody>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </PageBody>
    );
  }

  const profile = profileQuery.data;

  return (
    <>
      <PageHeader
        title="Meu perfil e segurança"
        description="Dados da sua conta e controles de acesso. Alterações em nome, cargo e papel são feitas pelo administrador e ficam registradas na trilha."
      />

      <PageBody className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader headingLevel={2} title="Identificação" />
          <dl className="mt-4 grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-content-subtle">Nome</dt>
            <dd className="text-content">{profile?.user.displayName}</dd>
            <dt className="text-content-subtle">Usuário</dt>
            <dd className="font-mono text-content">{profile?.user.username}</dd>
            <dt className="text-content-subtle">E-mail</dt>
            <dd className="text-content">{profile?.user.email}</dd>
            <dt className="text-content-subtle">Cargo</dt>
            <dd className="text-content">{profile?.user.jobTitle}</dd>
            <dt className="text-content-subtle">Papel</dt>
            <dd className="text-content">
              {profile ? SYSTEM_ROLE_LABEL[profile.role] : '—'}
            </dd>
            <dt className="text-content-subtle">Organização</dt>
            <dd className="text-content">{user?.organizationName}</dd>
            <dt className="text-content-subtle">Departamentos</dt>
            <dd className="text-content">
              {profile && profile.departments.length > 0 ? (
                <ul className="flex flex-wrap gap-1">
                  {profile.departments.map((dept) => (
                    <li key={dept.id}>
                      <Badge tone="outline" size="sm">
                        {dept.name}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                'Nenhum departamento vinculado'
              )}
            </dd>
          </dl>

          <p className="mt-4 flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-xs text-info-content">
            <IconInfo size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Seu <strong className="font-semibold">cargo</strong> é congelado em cada assinatura
              eletrônica que você aplica, como exigido pelo 21 CFR §11.50(a). Por isso ele só pode
              ser alterado pelo administrador, com registro em auditoria.
            </span>
          </p>
        </Card>

        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader headingLevel={2} title="Senha" />
            <dl className="mt-4 grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-content-subtle">Última troca</dt>
              <dd className="text-content qc-tabular">
                {profile?.user.passwordChangedAt
                  ? `${formatRegulatedDateTime(profile.user.passwordChangedAt, timeZone)} (${formatRelative(
                      profile.user.passwordChangedAt,
                    )})`
                  : 'Nunca trocada'}
              </dd>
              <dt className="text-content-subtle">Último acesso</dt>
              <dd className="text-content qc-tabular">
                {profile?.user.lastLoginAt
                  ? formatRegulatedDateTime(profile.user.lastLoginAt, timeZone)
                  : '—'}
              </dd>
            </dl>

            <div className="mt-4">
              <Button asChild variant="secondary" leadingIcon={<IconKey size={15} />}>
                <Link to="/trocar-senha">Trocar minha senha</Link>
              </Button>
            </div>
          </Card>

          <Card>
            <CardHeader
              headingLevel={2}
              title="Segundo fator de autenticação"
              description="Aumenta a segurança da entrada e, quando a organização exige, também da assinatura eletrônica."
            />
            <div className="mt-4">
              <Button
                variant="secondary"
                leadingIcon={<IconShieldCheck size={15} />}
                onClick={() => setEnrolling(true)}
              >
                Configurar segundo fator
              </Button>
            </div>
          </Card>
        </div>
      </PageBody>

      {enrolling ? <MfaEnrollmentDialog onClose={() => setEnrolling(false)} /> : null}
    </>
  );
}

function MfaEnrollmentDialog({ onClose }: { readonly onClose: () => void }) {
  const { toast } = useToast();
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);

  const enroll = useQuery({
    queryKey: ['mfa', 'enroll'],
    queryFn: () => authApi.enrollMfa(),
    // O segredo é sensível: uma única emissão por abertura do diálogo.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
  });

  const activate = useMutation({
    mutationFn: (code: string) => authApi.activateMfa(code),
    onError: (mutationError) => setError(toUserMessage(mutationError)),
    onSuccess: () => {
      toast({
        tone: 'success',
        title: 'Segundo fator ativado',
        description:
          'A partir de agora o código do aplicativo será pedido na entrada e nas assinaturas que exigirem segundo fator.',
      });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Configurar segundo fator"
      description="Cadastre o segredo em um aplicativo autenticador e confirme com um código válido."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={activate.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={activate.isPending}
            disabled={!/^\d{6}$/.test(totp)}
            onClick={() => activate.mutate(totp)}
          >
            Ativar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <FormError>{error}</FormError> : null}

        {enroll.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : enroll.data ? (
          <>
            <Field
              label="Chave secreta"
              description="Digite esta chave no seu aplicativo autenticador, ou use o endereço abaixo para gerar o QR Code. Guarde-a em local seguro: ela não será exibida novamente."
            >
              <Input readOnly value={enroll.data.secret} className="font-mono" />
            </Field>

            <Field label="Endereço de configuração">
              <Input readOnly value={enroll.data.otpauthUri} className="font-mono text-xs" />
            </Field>

            <Field
              label="Código de confirmação"
              required
              description="Informe o código de 6 dígitos que o aplicativo está exibindo agora."
            >
              <Input
                value={totp}
                onChange={(event) => setTotp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="w-36 text-center font-mono text-lg tracking-[0.4em]"
              />
            </Field>
          </>
        ) : (
          <FormError>
            Não foi possível iniciar o cadastro do segundo fator. Tente novamente em alguns
            instantes.
          </FormError>
        )}
      </div>
    </Dialog>
  );
}
